import { Trophy, TrendingUp, ListChecks, ArrowRight, Activity, Globe, RefreshCw } from 'lucide-react'
import { usePublicStats } from '@/hooks/usePublicStats'
import { MetricsModal, useMetricsModal } from '@/components/MetricsModal'
import { formatPercent, TELEGRAM_BOT_URL } from '@/lib/constants'
import { useScrollReveal } from '@/hooks/useScrollReveal'

function PerformanceCard({
  icon: Icon,
  label,
  value,
  context,
}: {
  icon: typeof Trophy
  label: string
  value: string
  context: string
}) {
  return (
    <div className="card text-center space-y-2 border-brand/20 bg-panel/80 hover:border-brand/40 transition duration-300">
      <div className="h-12 w-12 rounded-2xl bg-brand-soft flex items-center justify-center mx-auto">
        <Icon className="h-6 w-6 text-brand" />
      </div>
      <p className="font-display text-4xl font-extrabold tabular-nums text-text">{value}</p>
      <p className="text-sm font-bold text-text-dim">{label}</p>
      <p className="text-xs text-text-dim/70">{context}</p>
    </div>
  )
}

function VolumeBadge({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Activity
  label: string
  value: string
}) {
  return (
    <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-panel-elevated/40 border border-border">
      <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-border flex items-center justify-center">
        <Icon className="h-4 w-4 text-text-dim" />
      </div>
      <div className="text-left">
        <p className="text-sm font-extrabold text-text">{value}</p>
        <p className="text-[11px] text-text-dim">{label}</p>
      </div>
    </div>
  )
}

export function ResultsSection() {
  const { data, isLoading } = usePublicStats()
  const perf = data?.top_pick_performance
  const hasData = !isLoading && perf && perf.n_picks > 0
  const { openModal, closeModal, isOpen } = useMetricsModal()
  const ref = useScrollReveal<HTMLElement>()

  return (
    <>
      {isOpen && <MetricsModal onClose={closeModal} />}
      <section ref={ref} id="resultados" className="max-w-5xl mx-auto px-5 py-20">
        
        {/* Cabecera unificada (Estática, se revela bien) */}
        <div className="text-center mb-10 reveal">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand mb-2">
            Rendimiento y Datos
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold">
            Números reales, no promesas
          </h2>
          <p className="mt-3 text-text-dim max-w-lg mx-auto text-sm leading-relaxed">
            Métricas auditadas calculadas sobre el Top Pick del día. Los mismos datos reales 
            que ven los suscriptores activos dentro de la aplicación de Telegram.
          </p>
        </div>

        {/* Wrapper estático principal para todo el contenido dinámico e inferior. 
            Al tener la clase 'reveal', todo el bloque se revela de una vez y evita que 
            los elementos asíncronos queden con opacity-0. */}
        <div className="reveal space-y-8" style={{ transitionDelay: '100ms' }}>
          
          {/* Audit note explicativo */}
          {hasData && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm">
              <div className="px-4 py-2.5 rounded-xl bg-brand-soft border border-brand/20 text-center">
                <span className="text-brand font-semibold">
                  ✓ Métricas actualizadas automáticamente al liquidar cada pick
                </span>
              </div>
              <button
                onClick={openModal}
                className="text-text-dim underline underline-offset-2 hover:text-brand transition-colors text-xs font-medium"
              >
                ¿Cómo se calculan estas estadísticas?
              </button>
            </div>
          )}

          {/* Estado de carga / Fallback */}
          {!hasData && (
            <div className="card text-center py-10 text-sm text-text-dim max-w-md mx-auto">
              {isLoading
                ? 'Cargando estadísticas del sistema…'
                : 'Todavía no hay suficientes Top Picks liquidados para publicar métricas.'}
            </div>
          )}

          {/* Rendimiento (KPIs principales) */}
          {hasData && perf && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <PerformanceCard
                icon={Trophy}
                label="Win rate"
                value={formatPercent(perf.win_rate)}
                context="Top Pick del día histórico"
              />
              <PerformanceCard
                icon={TrendingUp}
                label="ROI"
                value={formatPercent(perf.roi)}
                context="Sobre stake total apostado"
              />
              <PerformanceCard
                icon={ListChecks}
                label="Picks liquidados"
                value={String(perf.n_picks)}
                context="Auditados en base de datos"
              />
            </div>
          )}

          {/* Métricas de Volumen (Social Proof unificado) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mx-auto pt-4">
            <VolumeBadge
              icon={Activity}
              value="+5.000 partidos"
              label="Analizados por el Decision Engine"
            />
            <VolumeBadge
              icon={Globe}
              value="+20 ligas"
              label="Competiciones de fútbol internacional"
            />
            <VolumeBadge
              icon={RefreshCw}
              value="Actualización diaria"
              label="Procesado automático cada madrugada"
            />
          </div>

          {/* CTA unificado de la sección */}
          <div className="cta-strip pt-4">
            <a href={TELEGRAM_BOT_URL} target="_blank" rel="noopener noreferrer" className="btn-brand">
              Quiero acceder a estos resultados
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          
        </div>
      </section>
    </>
  )
}
